/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { toast } from "sonner";
import axios from "axios";
import {
  Users, Plus, Trash, PencilSimple, ShieldCheck, X, FloppyDisk,
  Buildings, CheckCircle, Warning,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLUE = "#0B2B66";
const GREEN = "#2D5A27";
const RED = "#B12A1E";

const GOV_ROLES = [
  ["state_authority", "State Authority"],
  ["district_officer", "District Officer"],
  ["lsgd_officer", "LSGD Officer"],
  ["ward_officer", "Ward Officer"],
  ["pollution_control_board", "Pollution Control Board"],
  ["health_inspector", "Health Inspector"],
  ["swachh_bharat", "Swachh Bharat Official"],
];

const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Something went wrong";
};

const EMPTY_USER = () => ({
  name: "", email: "", phone: "", password: "",
  department: "", designation: "", government_id: "",
  gov_role: "district_officer",
  gov_states: [], gov_districts: [], gov_lsgds: [], gov_wards: [],
  status: "active",
});

export default function GovUserManagement() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loc, setLoc] = useState({ states: [], districts: [], lsgds: [], wards: [] });

  const load = async () => {
    try {
      const [u, l] = await Promise.all([
        api.get("/admin/gov-users"),
        api.get("/locations"),
      ]);
      setRows(u.data);
      const locs = l.data || [];
      setLoc({
        states: locs.filter(x => x.type === "state"),
        districts: locs.filter(x => x.type === "district"),
        lsgds: locs.filter(x => x.type === "lsgd"),
        wards: locs.filter(x => x.type === "ward"),
      });
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) {
        const { id, email, ...rest } = data;
        await api.put(`/admin/gov-users/${id}`, rest);
      } else {
        await api.post("/admin/gov-users", data);
      }
      toast.success("Saved · welcome email dispatched");
      setEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Deactivate this government user? They will no longer be able to log in.")) return;
    try { await api.delete(`/admin/gov-users/${id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const scopeLabel = (u) => {
    const s = (u.gov_states || []).length;
    const d = (u.gov_districts || []).length;
    const l = (u.gov_lsgds || []).length;
    const w = (u.gov_wards || []).length;
    if (w) return `${w} Ward${w > 1 ? "s" : ""}`;
    if (l) return `${l} LSGD${l > 1 ? "s" : ""}`;
    if (d) return `${d} District${d > 1 ? "s" : ""}`;
    if (s) return `${s} State${s > 1 ? "s" : ""}`;
    return "National (all)";
  };

  return (
    <AppShell>
      <div className="p-6 md:p-8 space-y-6" data-testid="gov-user-mgmt">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: RED }}>Government Regulatory System</div>
            <h1 className="font-heading text-3xl mt-1" style={{ color: BLUE }}>Government User Management</h1>
            <p className="text-sm text-[#5C5A55] mt-1">Create regulatory accounts with jurisdiction scoping. Each user can only access data within their assigned geography.</p>
          </div>
          <button onClick={() => setEditing(EMPTY_USER())} data-testid="gov-user-new"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md text-white text-sm font-medium" style={{ background: BLUE }}>
            <Plus size={14}/> New Government User
          </button>
        </header>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Total Users" v={rows.length} Icon={Users}/>
          <Kpi label="Active" v={rows.filter(r => r.status === "active").length} Icon={CheckCircle} color={GREEN}/>
          <Kpi label="Inactive" v={rows.filter(r => r.status !== "active").length} Icon={Warning}/>
          <Kpi label="Authorities" v={new Set(rows.map(r => r.gov_department).filter(Boolean)).size} Icon={Buildings}/>
        </div>

        <section className="bg-white rounded-xl border border-[#0B2B66]/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
                <th className="p-3 text-left font-semibold">Officer</th>
                <th className="p-3 text-left font-semibold">Department</th>
                <th className="p-3 text-left font-semibold">Role</th>
                <th className="p-3 text-left font-semibold">Jurisdiction</th>
                <th className="p-3 text-left font-semibold">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-b border-[#0B2B66]/5 hover:bg-[#0B2B66]/2" data-testid={`gov-row-${u.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-[10px] text-[#5C5A55]">{u.email} · {u.phone || "—"}</div>
                  </td>
                  <td className="p-3 text-xs">
                    <div>{u.gov_department || "—"}</div>
                    <div className="text-[10px] text-[#5C5A55]">{u.designation || ""}</div>
                  </td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold" style={{ background: "#EAF1FD", color: BLUE }}>
                      {(u.gov_role || "—").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="p-3 text-xs">{scopeLabel(u)}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold ${u.status === "active" ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#F4C7B9] text-[#7A2E18]"}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing(u)} className="w-7 h-7 rounded hover:bg-[#0B2B66]/10 text-[#5C5A55] hover:text-[#0B2B66] ml-1" title="Edit" data-testid={`gov-edit-${u.id}`}>
                      <PencilSimple size={12}/>
                    </button>
                    {u.status === "active" && (
                      <button onClick={() => del(u.id)} className="w-7 h-7 rounded hover:bg-red-50 text-[#5C5A55] hover:text-red-600 ml-1" title="Deactivate" data-testid={`gov-del-${u.id}`}>
                        <Trash size={12}/>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-sm text-[#5C5A55]">
                  No government users yet. Click <b>New Government User</b> to create the first one.
                </td></tr>
              )}
            </tbody>
          </table>
        </section>

        {editing && (
          <GovUserModal data={editing} loc={loc} onSave={save} onClose={() => setEditing(null)}/>
        )}
      </div>
    </AppShell>
  );
}

function GovUserModal({ data, loc, onSave, onClose }) {
  const [f, setF] = useState(data);
  const isEdit = !!f.id;

  // Cascading filters: districts filtered by selected states; lsgds by selected districts; wards by selected lsgds
  const filteredDistricts = useMemo(() => {
    if (f.gov_states.length === 0) return loc.districts;
    return loc.districts.filter(d => f.gov_states.includes(d.parent_id));
  }, [f.gov_states, loc.districts]);
  const filteredLsgds = useMemo(() => {
    if (f.gov_districts.length === 0) return loc.lsgds;
    return loc.lsgds.filter(l => f.gov_districts.includes(l.parent_id));
  }, [f.gov_districts, loc.lsgds]);
  const filteredWards = useMemo(() => {
    if (f.gov_lsgds.length === 0) return loc.wards;
    return loc.wards.filter(w => f.gov_lsgds.includes(w.parent_id));
  }, [f.gov_lsgds, loc.wards]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="gov-user-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#0B2B66]/10 sticky top-0 bg-white rounded-t-xl">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: RED }}>Regulatory Access</div>
            <h2 className="font-heading text-xl mt-0.5" style={{ color: BLUE }}>{isEdit ? "Edit Officer" : "Create Government Officer"}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded grid place-items-center hover:bg-[#0B2B66]/5" data-testid="modal-close"><X size={16}/></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="p-6 space-y-5">
          <Group title="Identity">
            <Grid2>
              <Fi label="Full Name" v={f.name} on={(v) => setF({ ...f, name: v })} required testid="gu-name"/>
              <Fi label="Email" type="email" v={f.email} on={(v) => setF({ ...f, email: v })} required disabled={isEdit} testid="gu-email"/>
              <Fi label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })}/>
              <Fi label={isEdit ? "New password (leave blank to keep)" : "Temporary password (min 8 chars)"} type="password" v={f.password} on={(v) => setF({ ...f, password: v })} required={!isEdit} testid="gu-password"/>
            </Grid2>
          </Group>
          <Group title="Authority">
            <Grid2>
              <Fi label="Department / Authority" v={f.department} on={(v) => setF({ ...f, department: v })} placeholder="e.g. Kerala State Pollution Control Board"/>
              <Fi label="Designation" v={f.designation} on={(v) => setF({ ...f, designation: v })} placeholder="e.g. District Officer"/>
              <Fi label="Government ID" v={f.government_id} on={(v) => setF({ ...f, government_id: v })} placeholder="e.g. KL-PCB-2024-001"/>
              <Fs label="Hierarchy Role" v={f.gov_role} on={(v) => setF({ ...f, gov_role: v })} opts={GOV_ROLES} testid="gu-role"/>
            </Grid2>
          </Group>
          <Group title="Jurisdiction Access Control"
                 sub="Leave all levels empty for unrestricted NATIONAL access. Select at any level to scope; deeper selections override shallower.">
            <JurisdictionPicker
              label="States" items={loc.states} value={f.gov_states}
              onChange={(v) => setF({ ...f, gov_states: v, gov_districts: [], gov_lsgds: [], gov_wards: [] })}
              testid="gu-states"/>
            <JurisdictionPicker
              label="Districts" items={filteredDistricts} value={f.gov_districts}
              onChange={(v) => setF({ ...f, gov_districts: v, gov_lsgds: [], gov_wards: [] })}
              testid="gu-districts"
              disabled={loc.states.length > 0 && f.gov_states.length === 0}/>
            <JurisdictionPicker
              label="LSGDs (Local Self Govt.)" items={filteredLsgds} value={f.gov_lsgds}
              onChange={(v) => setF({ ...f, gov_lsgds: v, gov_wards: [] })}
              testid="gu-lsgds"
              disabled={loc.districts.length > 0 && f.gov_districts.length === 0 && f.gov_states.length === 0}/>
            <JurisdictionPicker
              label="Wards" items={filteredWards} value={f.gov_wards}
              onChange={(v) => setF({ ...f, gov_wards: v })}
              testid="gu-wards"
              disabled={filteredLsgds.length === 0}/>
          </Group>
          <Group title="Status">
            <Fs label="Account status" v={f.status} on={(v) => setF({ ...f, status: v })}
                opts={[["active", "Active"], ["inactive", "Inactive"]]}/>
          </Group>
          <div className="flex items-center gap-3 pt-4 border-t border-[#0B2B66]/10">
            <button type="submit" data-testid="gu-save" className="h-11 px-6 rounded-md text-white text-sm font-medium inline-flex items-center gap-2" style={{ background: BLUE }}>
              <FloppyDisk size={14} weight="fill"/> {isEdit ? "Save changes" : "Create officer"}
            </button>
            <button type="button" onClick={onClose} className="h-11 px-4 text-sm text-[#5C5A55] hover:text-[#0B2B66]">Cancel</button>
            <div className="ml-auto text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: RED }}>
              <ShieldCheck size={11} className="inline mr-1"/> 2FA will be required on login
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const JurisdictionPicker = ({ label, items, value, onChange, testid, disabled }) => {
  const [search, setSearch] = useState("");
  const filtered = items.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()));
  const allSelected = items.length > 0 && value.length === items.length;
  const toggle = (id) => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  return (
    <div className={`mb-4 ${disabled ? "opacity-50 pointer-events-none" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55]">
          {label} <span className="text-[#8A8780] ml-1">· {value.length || 0} selected</span>
        </label>
        <div className="flex items-center gap-2 text-[10px]">
          <button type="button" onClick={() => onChange(items.map(i => i.id))} className="text-[#0B2B66] hover:underline">All</button>
          <span className="text-[#D0CFCF]">·</span>
          <button type="button" onClick={() => onChange([])} className="text-[#5C5A55] hover:underline">Clear</button>
        </div>
      </div>
      <div className="border border-[#0B2B66]/15 rounded-md max-h-40 overflow-y-auto bg-[#FAFBFF]">
        {items.length > 8 && (
          <div className="sticky top-0 bg-[#FAFBFF] p-1.5 border-b border-[#0B2B66]/10">
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder={`Search ${label.toLowerCase()}…`}
                   className="w-full h-7 px-2 text-xs rounded border border-[#0B2B66]/20 outline-none bg-white"/>
          </div>
        )}
        {filtered.length === 0 ? (
          <div className="p-3 text-xs text-[#5C5A55] italic">
            {items.length === 0 ? "Select a parent level first." : "No matches."}
          </div>
        ) : filtered.map(i => (
          <label key={i.id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-white border-b border-[#0B2B66]/5 last:border-b-0">
            <input type="checkbox" checked={value.includes(i.id)} onChange={() => toggle(i.id)} className="accent-[#0B2B66]"/>
            <span className="flex-1">{i.name}</span>
          </label>
        ))}
      </div>
      {allSelected && items.length > 0 && (
        <div className="text-[10px] text-[#2D5A27] mt-1">✓ All {items.length} selected — equivalent to "all {label.toLowerCase()}"</div>
      )}
    </div>
  );
};

const Kpi = ({ label, v, Icon, color }) => (
  <div className="bg-white rounded-lg border border-[#0B2B66]/10 p-4">
    <div className="flex items-center justify-between">
      <Icon size={16} weight="duotone" color={color || BLUE}/>
    </div>
    <div className="font-heading text-2xl mt-2" style={{ color: color || BLUE }}>{v}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mt-0.5">{label}</div>
  </div>
);

const Group = ({ title, sub, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5C5A55] mb-3">{title}</div>
    {sub && <div className="text-[11px] text-[#5C5A55] mb-3 bg-[#FAFBFF] p-2.5 rounded border border-[#0B2B66]/10">{sub}</div>}
    {children}
  </div>
);
const Grid2 = ({ children }) => <div className="grid md:grid-cols-2 gap-3">{children}</div>;
const Fi = ({ label, v, on, type = "text", required, placeholder, testid, disabled }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>
    <input type={type} value={v || ""} onChange={(e) => on(e.target.value)}
           required={required} placeholder={placeholder} disabled={disabled} data-testid={testid}
           className="w-full h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66] disabled:bg-[#F7F6F2]"/>
  </div>
);
const Fs = ({ label, v, on, opts, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>
    <select value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testid}
            className="w-full h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66] bg-white">
      {opts.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  </div>
);
